/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { IResourceId, IResourceMap, IResourceProps, IResourceStore } from 'apmui/resource';
import ResourceType from 'apmui/resource/ResourceType';
import Object from 'apmui/util/Object';
import WebtopFolderModel from './Model';

type FolderId = string;

/**
 * Strip leading, trailing and duplicate slashes
 */
function normalize(folder: string): FolderId {
    return folder
        .replace(/^\\+/, '')
        .replace(/\\+$/, '')
        .replace(/\\{2,}/, '\\');
}

/**
 * Collect folder hierarchy from the list of new resources.
 * @param resourceMap resource configurations to be added to webtop
 * @returns List of folder paths and assigned resources
 */
function collectFolderIds(resourceMap: IResourceMap) {
    const ret: Record<FolderId, IResourceId[]> = {};

    function addToFolder(folderId: FolderId, typeId: IResourceId) {
        // console.log(`addToFolder ${typeId.type}:${typeId.id} -> ${folderId}`);

        if (!ret[folderId]) {
            ret[folderId] = [];
            // add new folder to parent
            const parentId = getParent(folderId);
            if (typeof parentId !== 'undefined') {
                addToFolder(parentId, { type: ResourceType.WEBTOP_FOLDER, id: folderId });
            }
        }
        ret[folderId].push(typeId);
    }

    for (const group of Object.values(resourceMap)) {
        if (group) {
            for (const { props } of Object.values(group.resources)) {
                if ('folder' in props && props.folder) {
                    addToFolder(normalize(props.folder), {
                        type: props.type as ResourceType,
                        id: props.id,
                    });
                }
            }
        }
    }

    return ret;
}

function createResourceProps(folderId: FolderId, resources: IResourceId[]): IResourceProps {
    return {
        assignedResources: resources,
        props: {
            id: folderId,
            caption: getName(folderId),
            custom_img_path: '',
            description: '',
            folder: getParent(folderId) || '',
            type: ResourceType.WEBTOP_FOLDER,
        },
    };
}

function getName(folderId: FolderId): string {
    return folderId.replace(/^.*\\/, '');
}

function getParent(folderId: FolderId): FolderId | undefined {
    const parentId = folderId.replace(/\\[^\\]+$/, '');
    if (parentId !== folderId) {
        return parentId;
    }
    return undefined;
}

function getFolderById(resources: IResourceStore, folderId: FolderId) {
    const folderResources = resources[ResourceType.WEBTOP_FOLDER];
    if (folderResources && folderResources[folderId]) {
        return folderResources[folderId];
    }
    return undefined;
}

function getParentFolder(folder: WebtopFolderModel, resources: IResourceStore) {
    const parentId = getParent(folder.id);
    if (typeof parentId !== 'undefined') {
        return getFolderById(resources, parentId);
    }
    return undefined;
}

export { FolderId, collectFolderIds, createResourceProps, getFolderById, getParent, getParentFolder };
